// Basic utility functions (security features removed)

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Sanitize input to prevent XSS
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

// Role-based permissions mapping
const ROLE_PERMISSIONS = {
  super_admin: ['*'], // All permissions
  director: [
    'view_dashboard',
    'manage_users',
    'manage_teams',
    'manage_countries',
    'manage_universities',
    'manage_courses',
    'manage_content',
    'view_reports',
    'manage_admissions',
    'manage_enquiries'
  ],
  accounts_manager: [
    'view_dashboard',
    'manage_admissions',
    'manage_enquiries',
    'view_reports',
    'manage_documents'
  ],
  AccountsManager: [
    'view_dashboard',
    'manage_admissions',
    'manage_enquiries',
    'view_reports',
    'manage_documents'
  ],
  documentation_manager: [
    'view_dashboard',
    'manage_documents',
    'verify_documents',
    'view_reports'
  ],
  DocumentsManager: [
    'view_dashboard',
    'manage_documents',
    'verify_documents',
    'view_reports'
  ],
  team_lead: [
    'view_dashboard',
    'manage_referral_partners',
    'manage_admissions',
    'manage_tasks',
    'view_reports'
  ],
  seo_manager: [
    'view_dashboard',
    'manage_seo',
    'manage_blogs',
    'manage_content',
    'view_reports'
  ],
  content_manager: [
    'view_dashboard',
    'manage_blogs',
    'manage_news',
    'manage_gallery',
    'manage_features',
    'manage_testimonials',
    'manage_content'
  ],
  referral_partner: [
    'view_dashboard',
    'manage_own_admissions',
    'view_own_data'
  ]
};

// Check user permissions
export const checkPermission = (userRole, requiredPermission) => {
  if (!userRole || !requiredPermission) return false;

  const userPermissions = ROLE_PERMISSIONS[userRole] || [];
  return userPermissions.includes('*') || userPermissions.includes(requiredPermission);
};

// Check if user can access specific section
export const canAccessSection = (userRole, section) => {
  const sectionPermissions = {
    dashboard: ['view_dashboard'],
    tasks: ['manage_tasks'],
    admissions: ['manage_admissions', 'manage_own_admissions'],
    enquiries: ['manage_enquiries'],
    documents: ['manage_documents', 'verify_documents'],
    users: ['manage_users'],
    teams: ['manage_teams'],
    countries: ['manage_countries'],
    universities: ['manage_universities'],
    courses: ['manage_courses'],
    blogs: ['manage_blogs', 'manage_content'],
    news: ['manage_news', 'manage_content'],
    seo: ['manage_seo'],
    gallery: ['manage_gallery', 'manage_content'],
    features: ['manage_features', 'manage_content'],
    testimonials: ['manage_testimonials', 'manage_content']
  };

  const requiredPerms = sectionPermissions[section] || [];
  if (requiredPerms.length === 0) return false;

  return requiredPerms.some(perm => checkPermission(userRole, perm));
};

// Get available sections for user role
export const getAvailableSections = (userRole) => {
  const allSections = {
    dashboard: { label: 'Dashboard', icon: 'dashboard' },
    tasks: { label: 'Tasks', icon: 'tasks' },
    admissions: { label: 'Admissions', icon: 'admissions' },
    enquiries: { label: 'Enquiries', icon: 'enquiries' },
    documents: { label: 'Document Verification', icon: 'documents' },
    users: { label: 'Users', icon: 'users' },
    teams: { label: 'Teams', icon: 'teams' },
    countries: { label: 'Countries', icon: 'countries' },
    universities: { label: 'Universities', icon: 'universities' },
    courses: { label: 'Courses', icon: 'courses' },
    blogs: { label: 'Blogs', icon: 'blogs' },
    news: { label: 'News', icon: 'news' },
    seo: { label: 'SEO', icon: 'seo' },
    gallery: { label: 'Gallery', icon: 'gallery' },
    features: { label: 'Features', icon: 'features' },
    testimonials: { label: 'Testimonials', icon: 'testimonials' }
  };

  return Object.entries(allSections).filter(([key]) =>
    canAccessSection(userRole, key)
  ).reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
};